import {
	Checkbox,
	Grid,
	InputAdornment,
	IconButton,
	Stack,
	styled,
	TextField,
	Typography,
} from '@mui/material';

import { useEffect, useState } from 'react';

import { ReactComponent as CloseIcon } from 'common/assets/images/close.svg';
import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import AccordionComp from 'common/components/Accordion/AccordionComp';
import ButtonComp from 'common/components/Button/ButtonComp';
import { scrollProps, toSentenceCase } from 'common/utils/utilFunctions';

import { IConfirmationDialog, IPolicyDocument } from '../api/type-def';

import styles from './PolicySelectDocumentModal.module.scss';

const { searchFieldBackground, searchFieldBorderRadius } = styles;

export const StyledTextField = styled(TextField)({
	background: searchFieldBackground,
	borderRadius: searchFieldBorderRadius,
	width: '100%',
});

const PolicySelectDocumentModal = (props: IConfirmationDialog) => {
	const { checkboxCheckedcolor, checkboxUncheckedcolor } = styles;

	const { cancelClicked, submitClicked, checkBoxInfo, selectedFiles } = props;
	const [isPolicyAccordionOpen, setIsPolicyAccordionOpen] =
		useState<boolean>(true);
	const [selectedCheckBox, setSelectedCheckBox] =
		useState<string[]>(selectedFiles);
	const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);
	const [searchQuery, setSearchQuery] = useState<string>('');

	useEffect(() => {
		const allChecked = selectedCheckBox.length === checkBoxInfo.length;
		setSelectAllChecked(allChecked);
	}, [selectedCheckBox, checkBoxInfo]);

	const handleCheckBox = (
		fileName: string,
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		let updatedSelectedCheckBox: string[];
		if (event.target.checked) {
			updatedSelectedCheckBox = [...selectedCheckBox, fileName];
		} else {
			updatedSelectedCheckBox = selectedCheckBox.filter(
				(item) => item !== fileName,
			);
		}
		setSelectedCheckBox(updatedSelectedCheckBox);
	};

	const handleSelectAllCheckBox = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const isChecked = event.target.checked;
		setSelectAllChecked(isChecked);
		setSelectedCheckBox(
			isChecked ? checkBoxInfo?.map((doc: IPolicyDocument) => doc.name) : [],
		);
	};

	const filteredDocuments = checkBoxInfo?.filter((document: IPolicyDocument) =>
		document.name.toLowerCase().includes(searchQuery.toLowerCase()),
	);

	const handleSelectButtonClick = () => {
		if (submitClicked) {
			submitClicked(selectedCheckBox);
		}
	};

	const filteredDocumentsSorted = filteredDocuments
		.slice()
		.sort((a, b) => a.name.localeCompare(b.name));

	return (
		<Grid container direction="column" gap={2}>
			<Stack
				direction="row"
				className={styles['modal-heading']}
				justifyContent="space-between"
			>
				<Typography variant="h6">
					{toSentenceCase('Policy & Procedure Documents')}
				</Typography>
				<Stack direction="row" justifyContent="end">
					<IconButton onClick={cancelClicked}>
						<CloseIcon className={styles['close-modal-icon']} />
					</IconButton>
				</Stack>
			</Stack>
			<Grid item container display="flex" justifyContent="space-between">
				{checkBoxInfo.length > 1 && (
					<Grid
						item
						sm={4}
						md={4}
						alignItems="center"
						display="flex"
						justifyContent="flex-start"
					>
						<Checkbox
							checked={selectAllChecked}
							onChange={handleSelectAllCheckBox}
							sx={{
								color: checkboxUncheckedcolor,
								'&.Mui-checked': { color: checkboxCheckedcolor },
								'&.MuiCheckbox-indeterminate': { color: checkboxCheckedcolor },
							}}
							disabled={
								filteredDocuments.length === 0 || searchQuery.length !== 0
							}
						/>
						<Typography>{toSentenceCase('Select All')}</Typography>
					</Grid>
				)}
				<Grid item sm={5} md={5}>
					<StyledTextField
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchOutlined className={styles['input-search-icon']} />
								</InputAdornment>
							),
							inputProps: { 'data-testid': 'txt-policy-search' },
						}}
						placeholder="Search"
						size="small"
						variant="outlined"
						className={styles['policy-search']}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
					/>
				</Grid>
			</Grid>
			<Grid
				item
				xs={12}
				className={styles['policy-search-details']}
				sx={scrollProps('y-axis')}
			>
				<AccordionComp
					dataTestId="policy-accordion-comp"
					className={styles['accordian-header']}
					detailsStyle={styles['accordian-detail']}
					parentDivStyle={styles['accordian-parent-grid']}
					onChange={() => {
						setIsPolicyAccordionOpen(!isPolicyAccordionOpen);
					}}
					expanded={isPolicyAccordionOpen}
					summary={
						<Typography data-testid="obli-title">
							{toSentenceCase('Category Name')}
						</Typography>
					}
					details={
						filteredDocumentsSorted.length === 0 ? (
							<Typography>No data found</Typography>
						) : (
							filteredDocumentsSorted.map((document: IPolicyDocument) => (
								<Grid
									container
									alignItems="center"
									key={`${document.name}-checkbox`}
								>
									<Checkbox
										data-testid="modal-checkbox"
										checked={selectedCheckBox.includes(document.name)}
										onChange={(event) => handleCheckBox(document.name, event)}
										sx={{
											color: checkboxUncheckedcolor,
											'&.Mui-checked': { color: checkboxCheckedcolor },
											'&.MuiCheckbox-indeterminate': {
												color: checkboxCheckedcolor,
											},
										}}
									/>
									<Typography>{document.name}</Typography>
								</Grid>
							))
						)
					}
				/>
			</Grid>
			<Grid item xs={12}>
				<Grid container justifyContent="flex-end" spacing={2}>
					<Grid item>
						<ButtonComp btnType="secondary" clickHandler={cancelClicked}>
							Cancel
						</ButtonComp>
					</Grid>
					<Grid item>
						<ButtonComp
							btnType="primary"
							clickHandler={handleSelectButtonClick}
							disabled={selectedCheckBox.length === 0}
						>
							Select
						</ButtonComp>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default PolicySelectDocumentModal;
